// NewStreakInbox.jsx

import React from "react";
import { motion, AnimatePresence } from "framer-motion";

export const ClaimMessage = ({ claimMessage }) => {
    if (!claimMessage?.text) return null;

    const styles = {
        success: "bg-green-900/30 border-green-500/40 text-green-300",
        warning: "bg-yellow-900/30 border-yellow-500/40 text-yellow-300",
        error:   "bg-red-900/30   border-red-500/40   text-red-300",
        info:    "bg-cyan-900/30  border-cyan-500/40  text-cyan-300",
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl border p-3 text-xs font-medium ${styles[claimMessage.type] || styles.info}`}
        >
            {claimMessage.text}
        </motion.div>
    );
};

// ── NewStreakCard ─────────────────────────────────────────────────────────────
export const NewStreakCard = ({ item, claimStreak, claimingId }) => {
    const isClaiming = claimingId === item._id;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-3 flex items-center justify-between gap-3"
        >
            <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white truncate">
                    {item.particulars}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                    {item.streakType}
                    <span className="ml-2 text-yellow-400 font-bold">+{item.points} pts</span>
                </div>
            </div>

            <motion.button
                whileTap={{ scale: 0.92 }}
                onClick={() => claimStreak(item)}
                disabled={isClaiming}
                className="flex-shrink-0 w-24 px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-black text-xs font-black rounded-xl disabled:opacity-60 shadow-sm shadow-green-900/40"
            >
                {isClaiming ? "⏳" : "🎁 Claim"}
            </motion.button>
        </motion.div>
    );
};

// ── NewStreakInbox ────────────────────────────────────────────────────────────
const NewStreakInbox = ({ newStreaks, claimMessage, claimStreak, claimingId, setSelectedTab }) => {
    return (
        <div className="space-y-3 bg-gray-900 p-2 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="flex items-center gap-2">
                <div className="text-xs text-cyan-300 font-bold">⚡ New Streaks Inbox</div>
                {newStreaks.length > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-bold">
                        {newStreaks.length}
                    </span>
                )}
            </div>

            <ClaimMessage claimMessage={claimMessage} />

            <AnimatePresence>
                {newStreaks.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-gray-900/60 border border-cyan-500/20 rounded-2xl p-6 text-center"
                    >
                        <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            className="text-4xl mb-3"
                        >
                            🎉
                        </motion.div>
                        <div className="text-cyan-300 font-bold text-sm">All streaks claimed!</div>
                        <div className="text-gray-400 text-xs mt-1">
                            Your points are in the Streak Ledger.
                        </div>
                        <button
                            onClick={() => setSelectedTab("ledger")}
                            className="w-32 text-xs mt-2 px-2 py-1 rounded-xl bg-cyan-500 text-black font-black"
                        >
                            📒 View Ledger
                        </button>
                    </motion.div>
                ) : (
                    newStreaks.map((item) => (
                        <NewStreakCard
                            key={item._id}
                            item={item}
                            claimStreak={claimStreak}
                            claimingId={claimingId}
                        />
                    ))
                )}
            </AnimatePresence>
        </div>
    );
};

export default NewStreakInbox;
